import { Box, Button, FormControl, Input, Option, Select, Tab, TabList, TabPanel, Tabs, Typography } from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import React from "react";
import { Util } from "../Helpers/Util";
import FollowUpDialog from "../Dialogs/FollowUpDialog";
import ProposalTracking from "./ProposalTracking";
import PortalTableLayout from "../components/PortalTableLayout";

export interface FollowUp {
	BuildingName: string;
	FollowUpID: string;
	Address: string;
	Contact1Name: string;
	Contact1Email: string;
	Contact1Phone: string;
	Contact2Name: string;
	Contact2Email: string;
	Contact2Phone: string;
	Contact3Name: string;
	Contact3Email: string;
	Contact3Phone: string;
	DateAssessed?: Date;
	DateContract?: Date;
	DateLastUpdated: Date;
	FollowUps: string;
	Landings: string;
	Lost: boolean;
	Won: boolean;
	Maintenance: boolean;
	Modern: boolean;
	Repair: boolean;
	Notes: string;
	LastNote: string;
}
const tabPanelSx = {
	flex: 1,
	minHeight: 0,
	overflow: "auto",
	"&[hidden]": { display: "none" },
	"&:not([hidden])": { display: "flex", flexDirection: "column" },
};

// Main FollowUps component with tabs for Follow Ups and Proposal Tracking
export default function FollowUps() {
	const [tabIdx, setTabIdx] = React.useState(0);

	return (
		<Tabs
			value={tabIdx}
			onChange={(event, value) => setTabIdx(value as number)}
			sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
			<TabList>
				<Tab value={0}>Follow Ups</Tab>
				<Tab value={1}>Proposal Tracking</Tab>
			</TabList>
			<TabPanel value={0} sx={tabPanelSx}>
				<FollowUpsContent />
			</TabPanel>
			<TabPanel value={1} sx={tabPanelSx}>
				<ProposalTracking />
			</TabPanel>
		</Tabs>
	);
}

// Follow Ups content - extracted from original component
function FollowUpsContent() {
	const [data, setData] = React.useState<Array<FollowUp>>();
	const [pageNumber, setPageNumber] = React.useState(0);
	const [pageSize, setPageSize] = React.useState(25);
	const [globalFilter, setGlobalFilter] = React.useState("");
	const [order, setOrder] = React.useState(true);
	const [orderBy, setOrderBy] = React.useState(1);

	const [modal, setModal] = React.useState(false);
	const [modalFup, setModalFup] = React.useState<FollowUp>({
		FollowUpID: "",
		BuildingName: "",
		Address: "",
		Lost: false,
		Won: false,
		Maintenance: false,
		Modern: false,
		Repair: false,
		FollowUps: "",
		Landings: "",
		Notes: "",
		DateAssessed: new Date(),
		DateContract: new Date(),
		Contact1Name: "",
		Contact1Phone: "",
		Contact1Email: "",
		Contact2Name: "",
		Contact2Phone: "",
		Contact2Email: "",
		Contact3Name: "",
		Contact3Phone: "",
		Contact3Email: "",
	} as FollowUp);
	const [mode, setMode] = React.useState(0);

	React.useEffect(() => {
		getData();
	}, [mode]);

	React.useEffect(() => {
		setPageNumber(0);
	}, [mode, globalFilter]);

	const getData = () => {
		fetch("Inspection/GetFollowUps", {
			method: "Post",
			headers: {
				"Content-Type": "application/json",
			},
			cache: "no-cache",
			body: JSON.stringify(mode),
		})
			.then((x) => x.json())
			.then((x) => {
				const res = x.map((y: any) => {
					const splitNotes = y.follow_ups && y.follow_ups.split("\n");
					return {
						BuildingName: y.building_name,
						Address: y.address,
						Contact1Email: y.contact_1_email,
						Contact1Name: y.contact_1_name,
						Contact1Phone: y.contact_1_phone,
						Contact2Email: y.contact_2_email,
						Contact2Name: y.contact_2_name,
						Contact2Phone: y.contact_2_phone,
						Contact3Email: y.contact_3_email,
						Contact3Name: y.contact_3_name,
						Contact3Phone: y.contact_3_phone,
						DateAssessed: Util.ParseDate(y.date_assessed),
						DateContract: Util.ParseDate(y.date_contract),
						DateLastUpdated: Util.ParseDate(y.date_last_updated),
						FollowUpID: y.follow_up_id,
						FollowUps: y.follow_ups,
						Landings: y.landings,
						Lost: y.lost,
						Won: y.won,
						Maintenance: y.maint,
						Modern: y.modern,
						Notes: y.notes,
						Repair: y.repair,
						LastNote: splitNotes && splitNotes[splitNotes.length - 1],
					} as FollowUp;
				});
				setData(res);
			});
	};

	const markWon = (followUpId: string, event: React.MouseEvent) => {
		event.stopPropagation();
		fetch("Inspection/MarkFollowUpWon", {
			method: "Post",
			headers: {
				"Content-Type": "application/json",
			},
			cache: "no-cache",
			body: JSON.stringify(followUpId),
		}).then(() => getData());
	};

	const dt = (data ?? []).filter(
		(x) =>
			Util.isNullOrEmpty(globalFilter) ||
			x.BuildingName.toLowerCase().includes(globalFilter.toLowerCase()) ||
			x.FollowUps?.toLowerCase()?.includes(globalFilter.toLowerCase())
	);
	const cols = mode === 0 ? ["Building", "Date Attended", "Date of Contract", "Follow up notes", ""] : ["Building", "Date Attended", "Date of Contract", "Follow up notes"];
	const old = new Date("1/1/1900");
	dt.sort((a, b) => {
		switch (cols[orderBy]) {
			case "Building":
				return !order ? a.BuildingName.localeCompare(b.BuildingName) : b.BuildingName.localeCompare(a.BuildingName);
			case "Date Attended": {
				const ad = (a.DateAssessed ?? old).getTime();
				const bd = (b.DateAssessed ?? old).getTime();
				if ((ad > bd && !order) || (ad < bd && order)) return 1;
				if (ad === bd) return 0;
				return -1;
			}
			case "Date of Contract": {
				const aad = (a.DateContract ?? old).getTime();
				const bbd = (b.DateContract ?? old).getTime();
				if ((aad > bbd && !order) || (aad < bbd && order)) return 1;
				if (aad === bbd) return 0;
				return -1;
			}
			case "Follow up notes":
				return !order ? (a.FollowUps ?? "").localeCompare(b.FollowUps ?? "") : (b.FollowUps ?? "").localeCompare(a.FollowUps ?? "");
		}
		return 0;
	});
	const thisData = dt.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize);
	const rowCounts = [5, 10, 25, 50];

	const handleSort = (idx: number) => {
		setOrderBy(idx);
		setOrder(orderBy === idx ? !order : true);
	};

	const emptyFollowUp = {
		FollowUpID: "",
		BuildingName: "",
		Address: "",
		Lost: false,
		Won: false,
		Maintenance: false,
		Modern: false,
		Repair: false,
		FollowUps: "",
		Landings: "",
		Notes: "",
		DateAssessed: new Date(),
		DateContract: new Date(),
		Contact1Name: "",
		Contact1Phone: "",
		Contact1Email: "",
		Contact2Name: "",
		Contact2Phone: "",
		Contact2Email: "",
		Contact3Name: "",
		Contact3Phone: "",
		Contact3Email: "",
	} as FollowUp;

	return (
		<>
			<FollowUpDialog
				open={modal}
				followUp={modalFup}
				close={() => {
					setModal(false);
					setModalFup(emptyFollowUp);
					getData();
				}}
			/>
			<PortalTableLayout
				fillHeight={true}
				toolbar={
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 1.5,
							backgroundColor: "background.surface",
							border: "1px solid",
							borderColor: "divider",
							p: 1.5,
						}}>
						<Typography level="title-lg" sx={{ m: 0 }}>
							Follow Ups
						</Typography>
						<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
							<Select defaultValue={mode} onChange={(event, value) => setMode(value as number)}>
								<Option value={0}>Pending</Option>
								<Option value={1}>Lost</Option>
								<Option value={3}>Won</Option>
								<Option value={2}>All Buildings</Option>
							</Select>
							<Button
								startDecorator={<AddIcon />}
								onClick={() => {
									setModal(true);
								}}>
								Add Follow Up
							</Button>
							<FormControl>
								<Input
									name="Search"
									placeholder="Global Search"
									onChange={(event) => {
										setGlobalFilter(event.target.value);
									}}
									value={globalFilter}
								/>
							</FormControl>
						</Box>
					</Box>
				}
				pagination={{
					count: dt.length,
					page: pageNumber,
					rowsPerPage: pageSize,
					rowsPerPageOptions: rowCounts,
					onPageChange: (_event, newPage) => setPageNumber(newPage),
					onRowsPerPageChange: (event) => {
						setPageSize(parseInt(event.target.value, 10));
						setPageNumber(0);
					},
				}}>
				<Table stickyHeader size="small">
					<TableHead>
						<TableRow>
							{cols.map((x, idx) => (
								<TableCell key={idx} sortDirection={x && orderBy === idx ? (order ? "desc" : "asc") : false}>
									{x ? (
										<TableSortLabel active={orderBy === idx} direction={orderBy === idx ? (order ? "desc" : "asc") : "asc"} onClick={() => handleSort(idx)}>
											{x}
										</TableSortLabel>
									) : null}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{thisData?.map((x) => (
							<TableRow
								key={x.FollowUpID}
								hover
								sx={{ cursor: "pointer" }}
								onClick={() => {
									setModal(true);
									setModalFup(x);
								}}>
								<TableCell>{x.BuildingName}</TableCell>
								<TableCell>{Util.Formatter(x.DateAssessed, "D")}</TableCell>
								<TableCell>{Util.Formatter(x.DateContract, "D")}</TableCell>
								<TableCell>{x.FollowUps}</TableCell>
								{mode === 0 && (
									<TableCell>
										<Button
											size="sm"
											variant="solid"
											color="success"
											onClick={(event) => markWon(x.FollowUpID, event)}>
											Won
										</Button>
									</TableCell>
								)}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</PortalTableLayout>
		</>
	);
}
